import React, { useState, useEffect, useCallback } from 'react';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(process.env.REACT_APP_SUPABASE_URL, process.env.REACT_APP_SUPABASE_ANON_KEY);

const STRINGS = {
  en: {
    title: '📖 Vocabulary Notebook',
    subtitle: 'Save words, search and review with spaced repetition',
    back: '← Portal',
    addWord: '+ Add word',
    searchPlaceholder: 'Search words...',
    tabs: { all: 'All', due: 'Due today', learning: 'Learning', reviewing: 'Reviewing', mastered: 'Mastered' },
    empty: 'No words yet.',
    emptyDue: 'No words due today.',
    emptyFilter: 'No words match this filter.',
    addTitle: 'Add a new word',
    editTitle: 'Edit word',
    wordLabel: 'Spanish word or phrase',
    wordPlaceholder: 'e.g. ponerse al día',
    translationLabel: 'Meaning / translation',
    translationPlaceholder: 'e.g. to get up to speed',
    notesLabel: 'Notes (optional)',
    notesPlaceholder: 'Context, example sentence, memory tip...',
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    confirmDelete: 'Delete this word?',
    status: { learning: 'Learning', reviewing: 'Reviewing', mastered: 'Mastered' },
    reviewTitle: 'Daily Review',
    reviewDue: 'word due for review',
    reviewDuePlural: 'words due for review',
    reviewBtn: 'Start review →',
    reviewPrompt: 'Do you remember the meaning of:',
    forgot: '😅 Forgot',
    remembered: '✅ Remembered',
    reviewDone: 'Review complete!',
    reviewDoneMsg: 'Come back tomorrow for more.',
    nextReview: 'Next review:',
    added: 'Added',
    noSource: 'Added manually',
    today: 'Today',
    tomorrow: 'Tomorrow',
    daysLeft: 'days',
  },
  pt: {
    title: '📖 Caderneta de Vocabulário',
    subtitle: 'Salve palavras, busque e revise com spaced repetition',
    back: '← Portal',
    addWord: '+ Adicionar palavra',
    searchPlaceholder: 'Buscar palavras...',
    tabs: { all: 'Todas', due: 'Revisar hoje', learning: 'Aprendendo', reviewing: 'Revisando', mastered: 'Dominadas' },
    empty: 'Nenhuma palavra ainda.',
    emptyDue: 'Nenhuma palavra para revisar hoje.',
    emptyFilter: 'Nenhuma palavra neste filtro.',
    addTitle: 'Adicionar palavra',
    editTitle: 'Editar palavra',
    wordLabel: 'Palavra ou frase em espanhol',
    wordPlaceholder: 'ex: ponerse al día',
    translationLabel: 'Significado / tradução',
    translationPlaceholder: 'ex: se atualizar / colocar-se a par',
    notesLabel: 'Notas (opcional)',
    notesPlaceholder: 'Contexto, frase de exemplo, dica de memória...',
    save: 'Salvar',
    cancel: 'Cancelar',
    delete: 'Excluir',
    confirmDelete: 'Excluir esta palavra?',
    status: { learning: 'Aprendendo', reviewing: 'Revisando', mastered: 'Dominada' },
    reviewTitle: 'Revisão Diária',
    reviewDue: 'palavra para revisar',
    reviewDuePlural: 'palavras para revisar',
    reviewBtn: 'Iniciar revisão →',
    reviewPrompt: 'Você lembra o significado de:',
    forgot: '😅 Esqueci',
    remembered: '✅ Lembrei',
    reviewDone: 'Revisão concluída!',
    reviewDoneMsg: 'Volte amanhã para mais.',
    nextReview: 'Próxima revisão:',
    added: 'Adicionado',
    noSource: 'Adicionado manualmente',
    today: 'Hoje',
    tomorrow: 'Amanhã',
    daysLeft: 'dias',
  },
  es: {
    title: '📖 Libreta de Vocabulario',
    subtitle: 'Guarda palabras, busca y repasa con spaced repetition',
    back: '← Portal',
    addWord: '+ Agregar palabra',
    searchPlaceholder: 'Buscar palabras...',
    tabs: { all: 'Todas', due: 'Repasar hoy', learning: 'Aprendiendo', reviewing: 'Repasando', mastered: 'Dominadas' },
    empty: 'Aún no hay palabras.',
    emptyDue: 'No hay palabras para repasar hoy.',
    emptyFilter: 'Ninguna palabra en este filtro.',
    addTitle: 'Agregar palabra',
    editTitle: 'Editar palabra',
    wordLabel: 'Palabra o frase en español',
    wordPlaceholder: 'ej: ponerse al día',
    translationLabel: 'Significado / traducción',
    translationPlaceholder: 'ej: to get up to speed',
    notesLabel: 'Notas (opcional)',
    notesPlaceholder: 'Contexto, frase de ejemplo, truco para recordar...',
    save: 'Guardar',
    cancel: 'Cancelar',
    delete: 'Eliminar',
    confirmDelete: '¿Eliminar esta palabra?',
    status: { learning: 'Aprendiendo', reviewing: 'Repasando', mastered: 'Dominada' },
    reviewTitle: 'Repaso Diario',
    reviewDue: 'palabra para repasar',
    reviewDuePlural: 'palabras para repasar',
    reviewBtn: 'Iniciar repaso →',
    reviewPrompt: '¿Recuerdas el significado de:',
    forgot: '😅 No recordé',
    remembered: '✅ Lo recuerdo',
    reviewDone: '¡Repaso completado!',
    reviewDoneMsg: 'Vuelve mañana para más.',
    nextReview: 'Próximo repaso:',
    added: 'Agregado',
    noSource: 'Agregado manualmente',
    today: 'Hoy',
    tomorrow: 'Mañana',
    daysLeft: 'días',
  },
};

const STATUS_COLORS = {
  learning:  { bg: '#fef3c7', color: '#d97706', border: '#fde68a' },
  reviewing: { bg: '#dbeafe', color: '#1d4ed8', border: '#bfdbfe' },
  mastered:  { bg: '#dcfce7', color: '#16a34a', border: '#86efac' },
};

// Spaced repetition intervals (in days)
const SR_INTERVALS = {
  learning:  { remembered: 3,  forgot: 1 },
  reviewing: { remembered: 7,  forgot: 2 },
  mastered:  { remembered: 21, forgot: 5 },
};

function nextStatus(current, remembered) {
  if (!remembered) return 'learning';
  if (current === 'learning') return 'reviewing';
  if (current === 'reviewing') return 'mastered';
  return 'mastered';
}

function addDays(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().split('T')[0];
}

function todayStr() {
  return new Date().toISOString().split('T')[0];
}

function formatNextReview(dateStr, S) {
  if (!dateStr) return '';
  const today = todayStr();
  const tomorrow = addDays(1);
  if (dateStr <= today) return S.today;
  if (dateStr === tomorrow) return S.tomorrow;
  const days = Math.ceil((new Date(dateStr) - new Date(today)) / 86400000);
  return `${days} ${S.daysLeft}`;
}

const vocabStyles = `
  .vocab-card { background: white; border-radius: 16px; padding: 16px 18px; border: 1.5px solid #f1f5f9; box-shadow: 0 2px 8px rgba(0,0,0,0.04); transition: box-shadow 0.15s; cursor: default; }
  .vocab-card:hover { box-shadow: 0 4px 16px rgba(0,0,0,0.08); }
  .vocab-input { width: 100%; padding: 12px 14px; border-radius: 12px; border: 1.5px solid #e2e8f0; outline: none; font-family: 'Plus Jakarta Sans', sans-serif; font-size: 0.9rem; box-sizing: border-box; transition: border-color 0.15s; }
  .vocab-input:focus { border-color: #0284c7; }
  .vocab-textarea { width: 100%; padding: 12px 14px; border-radius: 12px; border: 1.5px solid #e2e8f0; outline: none; font-family: 'Plus Jakarta Sans', sans-serif; font-size: 0.88rem; resize: vertical; box-sizing: border-box; line-height: 1.5; transition: border-color 0.15s; }
  .vocab-textarea:focus { border-color: #0284c7; }
  .vocab-tab { padding: 8px 14px; border-radius: 10px; border: none; font-weight: 700; font-size: 0.78rem; cursor: pointer; font-family: 'Plus Jakarta Sans', sans-serif; transition: all 0.15s; background: transparent; color: #64748b; white-space: nowrap; }
  .vocab-tab.active { background: white; color: #0f172a; box-shadow: 0 2px 8px rgba(0,0,0,0.08); }
  .modal-overlay { position: fixed; inset: 0; background: rgba(15,23,42,0.65); backdrop-filter: blur(8px); display: flex; align-items: flex-end; justify-content: center; z-index: 5000; }
  .modal-sheet { background: white; padding: 32px 24px 48px; border-radius: 28px 28px 0 0; width: 100%; max-width: 500px; max-height: 90vh; overflow-y: auto; animation: slideUp 0.3s cubic-bezier(0.16,1,0.3,1); }
  @keyframes slideUp { from { transform: translateY(100px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
  .review-card { background: linear-gradient(135deg, #f0f9ff, #e0f2fe); border: 2px solid #bae6fd; border-radius: 20px; padding: 32px 24px; text-align: center; margin-bottom: 20px; }
`;

export default function VocabES({ session, lang = 'pt', onBack }) {
  const S = STRINGS[lang] || STRINGS['pt'];
  const [words, setWords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterTab, setFilterTab] = useState('all');
  const [modalOpen, setModalOpen] = useState(false);
  const [editWord, setEditWord] = useState(null);
  const [form, setForm] = useState({ word: '', translation: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [reviewMode, setReviewMode] = useState(false);
  const [reviewQueue, setReviewQueue] = useState([]);
  const [reviewIdx, setReviewIdx] = useState(0);
  const [reviewFlipped, setReviewFlipped] = useState(false);
  const [reviewDone, setReviewDone] = useState(false);

  const loadWords = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from('vocabulary')
      .select('*')
      .eq('student_id', session.id)
      .order('created_at', { ascending: false });
    setWords(data || []);
    setLoading(false);
  }, [session.id]);

  useEffect(() => { loadWords(); }, [loadWords]);

  const openAdd = () => {
    setEditWord(null);
    setForm({ word: '', translation: '', notes: '' });
    setModalOpen(true);
  };

  const openEdit = (w) => {
    setEditWord(w);
    setForm({ word: w.word, translation: w.translation || '', notes: w.notes || '' });
    setModalOpen(true);
  };

  const saveWord = async () => {
    if (!form.word.trim()) return;
    setSaving(true);
    if (editWord) {
      await supabase.from('vocabulary').update({
        word: form.word.trim(),
        translation: form.translation.trim() || null,
        notes: form.notes.trim() || null,
      }).eq('id', editWord.id);
    } else {
      await supabase.from('vocabulary').insert({
        student_id: session.id,
        word: form.word.trim(),
        translation: form.translation.trim() || null,
        notes: form.notes.trim() || null,
        status: 'learning',
        next_review: todayStr(),
        review_count: 0,
      });
    }
    setSaving(false);
    setModalOpen(false);
    loadWords();
  };

  const deleteWord = async (id) => {
    if (!window.confirm(S.confirmDelete)) return;
    await supabase.from('vocabulary').delete().eq('id', id);
    loadWords();
  };

  const startReview = () => {
    const due = words.filter(w => w.next_review <= todayStr());
    setReviewQueue(due);
    setReviewIdx(0);
    setReviewFlipped(false);
    setReviewDone(false);
    setReviewMode(true);
  };

  const handleReview = async (remembered) => {
    const w = reviewQueue[reviewIdx];
    const newStatus = nextStatus(w.status, remembered);
    const interval = SR_INTERVALS[w.status][remembered ? 'remembered' : 'forgot'];
    const nextReview = addDays(interval);
    await supabase.from('vocabulary').update({
      status: newStatus,
      next_review: nextReview,
      review_count: (w.review_count || 0) + 1,
    }).eq('id', w.id);
    if (reviewIdx + 1 >= reviewQueue.length) {
      setReviewDone(true);
      loadWords();
    } else {
      setReviewIdx(i => i + 1);
      setReviewFlipped(false);
    }
  };

  const today = todayStr();
  const dueWords = words.filter(w => w.next_review <= today);

  const filtered = words.filter(w => {
    const matchSearch = !search ||
      w.word.toLowerCase().includes(search.toLowerCase()) ||
      (w.translation || '').toLowerCase().includes(search.toLowerCase()) ||
      (w.notes || '').toLowerCase().includes(search.toLowerCase());
    const matchTab = filterTab === 'all' ? true :
      filterTab === 'due' ? w.next_review <= today :
      w.status === filterTab;
    return matchSearch && matchTab;
  });

  const emptyMsg = filterTab === 'due' ? S.emptyDue : filtered.length === 0 && words.length > 0 ? S.emptyFilter : S.empty;

  if (reviewMode) {
    if (reviewDone) return (
      <>
        <style>{vocabStyles}</style>
        <div style={{ padding: '24px 20px 80px' }}>
          <button onClick={() => setReviewMode(false)} style={{ background: '#f1f5f9', border: 'none', borderRadius: '12px', padding: '10px 16px', fontWeight: '800', fontSize: '0.82rem', color: '#475569', cursor: 'pointer', fontFamily: 'Plus Jakarta Sans', marginBottom: '32px' }}>{S.back}</button>
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <div style={{ fontSize: '4rem', marginBottom: '16px' }}>🎉</div>
            <div style={{ fontSize: '1.4rem', fontWeight: '900', color: '#0f172a', marginBottom: '8px' }}>{S.reviewDone}</div>
            <div style={{ fontSize: '0.88rem', color: '#64748b', fontWeight: '600' }}>{S.reviewDoneMsg}</div>
            <button onClick={() => setReviewMode(false)} style={{ marginTop: '32px', padding: '14px 32px', background: 'linear-gradient(135deg,#0284c7,#0369a1)', color: 'white', border: 'none', borderRadius: '14px', fontWeight: '800', fontSize: '0.95rem', cursor: 'pointer', fontFamily: 'Plus Jakarta Sans' }}>← {S.back.replace('← ', '')}</button>
          </div>
        </div>
      </>
    );

    const current = reviewQueue[reviewIdx];
    return (
      <>
        <style>{vocabStyles}</style>
        <div style={{ padding: '24px 20px 80px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
            <button onClick={() => setReviewMode(false)} style={{ background: '#f1f5f9', border: 'none', borderRadius: '12px', padding: '10px 16px', fontWeight: '800', fontSize: '0.82rem', color: '#475569', cursor: 'pointer', fontFamily: 'Plus Jakarta Sans' }}>{S.back}</button>
            <div style={{ fontSize: '0.78rem', fontWeight: '700', color: '#64748b' }}>{reviewIdx + 1} / {reviewQueue.length}</div>
          </div>
          <div style={{ height: '6px', background: '#e0f2fe', borderRadius: '99px', marginBottom: '28px', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${(reviewIdx / reviewQueue.length) * 100}%`, background: '#0284c7', borderRadius: '99px', transition: 'width 0.3s' }} />
          </div>
          <div className="review-card" onClick={() => setReviewFlipped(f => !f)} style={{ cursor: 'pointer', minHeight: '200px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            {!reviewFlipped ? (
              <>
                <div style={{ fontSize: '0.7rem', fontWeight: '800', color: '#0284c7', textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '16px' }}>{S.reviewPrompt}</div>
                <div style={{ fontSize: '2rem', fontWeight: '900', color: '#0f172a', textAlign: 'center' }}>{current.word}</div>
                <div style={{ fontSize: '0.78rem', color: '#94a3b8', fontWeight: '600', marginTop: '16px' }}>
                  {lang === 'es' ? 'Toca para ver el significado' : lang === 'en' ? 'Tap to reveal meaning' : 'Toque para ver o significado'}
                </div>
              </>
            ) : (
              <>
                <div style={{ fontSize: '0.7rem', fontWeight: '800', color: '#0284c7', textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '12px' }}>
                  {lang === 'es' ? 'Significado' : lang === 'en' ? 'Meaning' : 'Significado'}
                </div>
                <div style={{ fontSize: '1.4rem', fontWeight: '800', color: '#0f172a', textAlign: 'center', marginBottom: '8px' }}>{current.translation || '—'}</div>
                {current.notes && <div style={{ fontSize: '0.82rem', color: '#64748b', fontStyle: 'italic', textAlign: 'center', marginTop: '8px', lineHeight: 1.5 }}>{current.notes}</div>}
              </>
            )}
          </div>
          {reviewFlipped && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <button onClick={() => handleReview(false)} style={{ padding: '18px', background: '#fff1f2', border: '2px solid #fecdd3', borderRadius: '16px', fontWeight: '800', fontSize: '0.95rem', color: '#be123c', cursor: 'pointer', fontFamily: 'Plus Jakarta Sans' }}>{S.forgot}</button>
              <button onClick={() => handleReview(true)} style={{ padding: '18px', background: '#f0fdf4', border: '2px solid #86efac', borderRadius: '16px', fontWeight: '800', fontSize: '0.95rem', color: '#15803d', cursor: 'pointer', fontFamily: 'Plus Jakarta Sans' }}>{S.remembered}</button>
            </div>
          )}
          {!reviewFlipped && (
            <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: '0.78rem', fontWeight: '600', marginTop: '16px' }}>
              {lang === 'es' ? 'Toca la tarjeta para revelar' : lang === 'en' ? 'Tap card to reveal' : 'Toque o card para revelar'}
            </div>
          )}
        </div>
      </>
    );
  }

  return (
    <>
      <style>{vocabStyles}</style>
      <div style={{ padding: '24px 20px 80px', maxWidth: '960px', margin: '0 auto' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
          <button onClick={onBack} style={{ background: '#f1f5f9', border: 'none', borderRadius: '12px', padding: '10px 16px', fontWeight: '800', fontSize: '0.82rem', color: '#475569', cursor: 'pointer', fontFamily: 'Plus Jakarta Sans', flexShrink: 0 }}>{S.back}</button>
          <div style={{ flex: 1 }} />
          <button onClick={openAdd} style={{ background: 'linear-gradient(135deg,#0284c7,#0369a1)', color: 'white', border: 'none', borderRadius: '12px', padding: '10px 14px', fontWeight: '800', fontSize: '0.78rem', cursor: 'pointer', fontFamily: 'Plus Jakarta Sans', flexShrink: 0, boxShadow: '0 4px 12px rgba(2,132,199,0.3)' }}>{S.addWord}</button>
        </div>

        {/* Hero explanation card */}
        <div style={{ background: 'linear-gradient(135deg,#0284c7 0%,#0369a1 60%,#1e3a8a 100%)', borderRadius: '20px', padding: '24px', marginBottom: '20px', position: 'relative', overflow: 'hidden', boxShadow: '0 8px 32px rgba(2,132,199,0.25)' }}>
          <div style={{ position: 'absolute', top: '-20px', right: '-20px', width: '120px', height: '120px', borderRadius: '50%', background: 'rgba(255,255,255,0.06)' }} />
          <div style={{ position: 'absolute', bottom: '-30px', right: '40px', width: '80px', height: '80px', borderRadius: '50%', background: 'rgba(255,255,255,0.04)' }} />
          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{ fontSize: '2rem', marginBottom: '10px' }}>📖</div>
            <div style={{ fontWeight: '900', color: 'white', fontSize: '1.15rem', marginBottom: '8px', lineHeight: 1.2 }}>
              {lang === 'es' ? 'Tu libreta personal de vocabulario' : lang === 'en' ? 'Your personal vocabulary notebook' : 'Sua caderneta pessoal de vocabulário'}
            </div>
            <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.82rem', fontWeight: '600', lineHeight: 1.6, marginBottom: '16px' }}>
              {lang === 'es'
                ? 'Guarda palabras y frases que encuentras en clase o quieres recordar. El sistema de repaso espaciado te las muestra en el momento justo — ni demasiado pronto, ni demasiado tarde.'
                : lang === 'en'
                ? 'Save words and phrases you encounter in class or want to remember. The spaced repetition system shows them to you at exactly the right moment — not too soon, not too late.'
                : 'Salve palavras e frases que encontrar na aula ou quiser lembrar. O sistema de revisão espaçada mostra elas no momento certo — nem cedo demais, nem tarde demais.'}
            </div>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              {[
                { icon: '🟡', label: lang === 'es' ? 'Aprendiendo → revisar en 3 días' : lang === 'en' ? 'Learning → review in 3 days' : 'Aprendendo → revisar em 3 dias' },
                { icon: '🔵', label: lang === 'es' ? 'Repasando → revisar en 7 días' : lang === 'en' ? 'Reviewing → review in 7 days' : 'Revisando → revisar em 7 dias' },
                { icon: '🟢', label: lang === 'es' ? 'Dominada → revisar en 21 días' : lang === 'en' ? 'Mastered → review in 21 days' : 'Dominada → revisar em 21 dias' },
              ].map(({ icon, label }) => (
                <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'rgba(255,255,255,0.1)', borderRadius: '8px', padding: '6px 10px' }}>
                  <span style={{ fontSize: '0.75rem' }}>{icon}</span>
                  <span style={{ fontSize: '0.72rem', fontWeight: '700', color: 'rgba(255,255,255,0.9)' }}>{label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Word count */}
        <div style={{ fontSize: '0.72rem', fontWeight: '700', color: '#94a3b8', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '1px' }}>
          {words.length} {lang === 'es' ? 'palabras guardadas' : lang === 'en' ? 'words saved' : 'palavras salvas'}
        </div>

        {/* Due for review banner */}
        {dueWords.length > 0 && !reviewMode && (
          <div style={{ background: 'linear-gradient(135deg,#0284c7,#0369a1)', borderRadius: '16px', padding: '16px 20px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '14px', boxShadow: '0 4px 16px rgba(2,132,199,0.25)' }}>
            <span style={{ fontSize: '1.8rem', flexShrink: 0 }}>🔔</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: '900', color: 'white', fontSize: '0.95rem', marginBottom: '2px' }}>
                {dueWords.length} {dueWords.length === 1 ? S.reviewDue : S.reviewDuePlural}
              </div>
              <div style={{ fontWeight: '600', color: 'rgba(255,255,255,0.75)', fontSize: '0.78rem' }}>
                {lang === 'es' ? 'El repaso toma solo unos minutos.' : lang === 'en' ? 'Review takes just a few minutes.' : 'A revisão leva só alguns minutos.'}
              </div>
            </div>
            <button onClick={startReview} style={{ background: 'white', color: '#0284c7', border: 'none', borderRadius: '10px', padding: '10px 16px', fontWeight: '800', fontSize: '0.82rem', cursor: 'pointer', fontFamily: 'Plus Jakarta Sans', flexShrink: 0 }}>{S.reviewBtn}</button>
          </div>
        )}

        {/* Search */}
        <input
          type="text"
          placeholder={S.searchPlaceholder}
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="vocab-input"
          style={{ marginBottom: '12px', background: 'white' }}
        />

        {/* Filter tabs */}
        <div style={{ display: 'flex', gap: '4px', background: '#f1f5f9', padding: '4px', borderRadius: '14px', marginBottom: '20px', overflowX: 'auto' }}>
          {Object.entries(S.tabs).map(([key, label]) => {
            const count = key === 'all' ? words.length : key === 'due' ? dueWords.length : words.filter(w => w.status === key).length;
            return (
              <button key={key} className={`vocab-tab ${filterTab === key ? 'active' : ''}`} onClick={() => setFilterTab(key)}>
                {label}{count > 0 ? ` (${count})` : ''}
              </button>
            );
          })}
        </div>

        {/* Word list */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8', fontWeight: '600' }}>
            {lang === 'es' ? 'Cargando...' : lang === 'en' ? 'Loading...' : 'Carregando...'}
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px 20px', color: '#94a3b8' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '12px' }}>📖</div>
            <div style={{ fontWeight: '700', fontSize: '0.95rem' }}>{emptyMsg}</div>
            {words.length === 0 && (
              <button onClick={openAdd} style={{ marginTop: '20px', padding: '12px 24px', background: 'linear-gradient(135deg,#0284c7,#0369a1)', color: 'white', border: 'none', borderRadius: '12px', fontWeight: '800', cursor: 'pointer', fontFamily: 'Plus Jakarta Sans' }}>{S.addWord}</button>
            )}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {filtered.map(w => {
              const sc = STATUS_COLORS[w.status] || STATUS_COLORS.learning;
              const isDue = w.next_review <= today;
              return (
                <div key={w.id} className="vocab-card" onClick={() => openEdit(w)}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '4px' }}>
                        <span style={{ fontWeight: '900', fontSize: '1rem', color: '#0f172a' }}>{w.word}</span>
                        <span style={{ background: sc.bg, color: sc.color, border: `1px solid ${sc.border}`, borderRadius: '50px', padding: '2px 8px', fontSize: '0.65rem', fontWeight: '800' }}>
                          {S.status[w.status] || w.status}
                        </span>
                        {isDue && <span style={{ background: '#fee2e2', color: '#dc2626', borderRadius: '50px', padding: '2px 8px', fontSize: '0.65rem', fontWeight: '800' }}>🔔 {S.tabs.due}</span>}
                      </div>
                      {w.translation && <div style={{ fontSize: '0.88rem', color: '#475569', fontWeight: '600', marginBottom: '3px' }}>{w.translation}</div>}
                      {w.notes && <div style={{ fontSize: '0.78rem', color: '#94a3b8', fontStyle: 'italic', lineHeight: 1.4 }}>{w.notes}</div>}
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontSize: '0.68rem', color: '#94a3b8', fontWeight: '600' }}>{S.nextReview}</div>
                      <div style={{ fontSize: '0.75rem', fontWeight: '800', color: isDue ? '#dc2626' : '#64748b' }}>{formatNextReview(w.next_review, S)}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Add/Edit modal */}
        {modalOpen && (
          <div className="modal-overlay" onClick={e => e.target.className === 'modal-overlay' && setModalOpen(false)}>
            <div className="modal-sheet">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <h2 style={{ margin: 0, fontWeight: '900', color: '#0f172a', fontSize: '1.2rem' }}>{editWord ? S.editTitle : S.addTitle}</h2>
                <button onClick={() => setModalOpen(false)} style={{ background: '#f1f5f9', border: 'none', borderRadius: '50%', width: '32px', height: '32px', cursor: 'pointer', fontSize: '1rem', color: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ fontSize: '0.75rem', fontWeight: '800', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '6px' }}>{S.wordLabel}</label>
                <input type="text" value={form.word} onChange={e => setForm(f => ({...f, word: e.target.value}))} placeholder={S.wordPlaceholder} className="vocab-input" autoFocus />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ fontSize: '0.75rem', fontWeight: '800', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '6px' }}>{S.translationLabel}</label>
                <input type="text" value={form.translation} onChange={e => setForm(f => ({...f, translation: e.target.value}))} placeholder={S.translationPlaceholder} className="vocab-input" />
              </div>
              <div style={{ marginBottom: '24px' }}>
                <label style={{ fontSize: '0.75rem', fontWeight: '800', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '6px' }}>{S.notesLabel}</label>
                <textarea value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} placeholder={S.notesPlaceholder} className="vocab-textarea" rows={3} />
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                {editWord && (
                  <button onClick={() => { deleteWord(editWord.id); setModalOpen(false); }} style={{ padding: '13px 16px', background: 'white', color: '#dc2626', border: '1.5px solid #fecdd3', borderRadius: '12px', fontWeight: '700', cursor: 'pointer', fontFamily: 'Plus Jakarta Sans' }}>{S.delete}</button>
                )}
                <button onClick={() => setModalOpen(false)} style={{ flex: 1, padding: '13px', background: 'white', color: '#64748b', border: '1.5px solid #e2e8f0', borderRadius: '12px', fontWeight: '700', cursor: 'pointer', fontFamily: 'Plus Jakarta Sans' }}>{S.cancel}</button>
                <button onClick={saveWord} disabled={saving || !form.word.trim()} style={{ flex: 2, padding: '13px', background: form.word.trim() ? 'linear-gradient(135deg,#0284c7,#0369a1)' : '#f1f5f9', color: form.word.trim() ? 'white' : '#94a3b8', border: 'none', borderRadius: '12px', fontWeight: '800', cursor: form.word.trim() ? 'pointer' : 'default', fontFamily: 'Plus Jakarta Sans' }}>{saving ? '...' : S.save}</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
